
import type { Doctor, DoctorAddress, InsuranceOperatorOption } from './data';

/** Coerce a raw JSON address object into the view-model shape (all string fields). */
function toAddress(a: Record<string, unknown>): DoctorAddress {
  const s = (k: string) => String(a?.[k] ?? '');
  return {
    id: s('id'),
    street: s('street'),
    number: s('number'),
    neighborhood: s('neighborhood'),
    city: s('city'),
    cep: s('cep'),
    complement: s('complement'),
  };
}

/** Expand a weekly availability template into a flat list of slot times (HH:MM). */
export function expandSlots(
  availability: { startTime: string; endTime: string; slotMinutes: number }[] | null | undefined,
  max = 6,
): { time: string }[] {
  if (!availability?.length) return [];
  const times: string[] = [];
  for (const a of availability) {
    const [sh, sm] = a.startTime.split(':').map(Number);
    const [eh, em] = a.endTime.split(':').map(Number);
    let mins = sh * 60 + sm;
    const end = eh * 60 + em;
    while (mins < end && times.length < max * 2) {
      times.push(
        `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`,
      );
      mins += a.slotMinutes || 30;
    }
  }
  return [...new Set(times)]
    .sort()
    .slice(0, max)
    .map((time) => ({ time }));
}

export interface DoctorDto {
  id: string;
  slug: string | null;
  specialty: string;
  council?: string;
  crm: string;
  approvalStatus?: string; // own-profile responses only
  seal?: { tone: 'red' | 'yellow' | 'gold'; key: string } | null;
  bookable?: boolean;
  bookingPaused?: boolean;
  payoutInfo?: { pixKey: string | null } | null; // own-profile responses only
  // own-profile responses only
  verification?: {
    docPaths: string[];
    submittedAt: string | null;
    presentationLetter?: string | null;
    reviewNote?: string | null;
  } | null;
  bio: string | null;
  priceCents: number;
  /** Optional during the API-before-web deploy window. */
  patientPriceCents?: number;
  verified: boolean;
  respondsFast?: boolean;
  telehealth: boolean;
  inPerson: boolean;
  experienceYears: number;
  location: string | null;
  timezone?: string;
  ratingAvg: number;
  ratingCount: number;
  user?: { fullName: string; avatarUrl: string | null; city: string | null } | null;
  insurances?:
    | {
        insurance: {
          id: string;
          name: string;
          aliases?: string[] | null;
          isPopular?: boolean | null;
          /** Detail payload only — the directory listing never carries this level. */
          operators?: InsuranceOperatorOption[] | null;
        } | null;
      }[]
    | null;
  specialties?: { specialty: { id: string; name: string } | null }[] | null;
  availability?: { startTime: string; endTime: string; slotMinutes: number }[] | null;
  // MED-7 enrichment
  videoUrl?: string | null;
  socialLinks?: Record<string, string> | null;
  consultationInfo?: Record<string, unknown> | null;
  languages?: string[] | null;
  mainConditions?: string[] | null;
  otherConditions?: string[] | null;
  education?: string | null;
  experiences?: string | null;
  awards?: string[] | null;
  publications?: string[] | null;
  whyBook?: string[] | null;
  aiSummary?: string | null;
  reviewThemes?: string[] | null;
  addresses?: Record<string, unknown>[] | null;
  photoUrls?: string[] | null;
  certificateUrls?: string[] | null;
  publicationLinks?: unknown;
  pressLinks?: unknown;
  faq?: unknown;
  resultPhotos?: unknown;
  videos?: unknown;
}

/** Json columns arrive untyped; trust the API's write-side validation for the shape. */
function asArray<T>(v: unknown): T[] {
  return Array.isArray(v) ? (v as T[]) : [];
}

export function toDoctor(row: DoctorDto): Doctor {
  return {
    id: row.id,
    slug: row.slug ?? undefined,
    name: row.user?.fullName ?? 'Profissional',
    specialty: row.specialty,
    specialties: (row.specialties ?? [])
      .map((s) => s.specialty)
      .filter((s): s is { id: string; name: string } => !!s)
      .map((s) => ({ id: s.id, name: s.name })),
    council: row.council ?? undefined,
    crm: row.crm,
    approvalStatus: row.approvalStatus,
    pixKey: row.payoutInfo?.pixKey ?? undefined,
    verificationDocPaths: row.verification?.docPaths ?? undefined,
    presentationLetter: row.verification?.presentationLetter ?? undefined,
    reviewNote: row.verification?.reviewNote ?? undefined,
    photo: row.user?.avatarUrl ?? '',
    rating: row.ratingAvg ?? 0,
    reviews: row.ratingCount ?? 0,
    location: row.location ?? row.user?.city ?? '',
    timezone: row.timezone ?? 'America/Sao_Paulo',
    priceCents: row.priceCents ?? 0,
    patientPriceCents: row.patientPriceCents ?? row.priceCents ?? 0,
    // Seal = marketplace approval. Do not OR the legacy `verified` column alone:
    // a CRM re-moderation can flip approvalStatus back to pending while leaving
    // verified true. Fall back to the column only when approvalStatus is absent.
    verified: row.approvalStatus != null ? row.approvalStatus === 'approved' : !!row.verified,
    seal: row.seal ? (row.seal as Doctor['seal']) : undefined,
    bookable: row.bookable,
    bookingPaused: row.bookingPaused,
    respondsFast: row.respondsFast ?? false,
    telehealth: row.telehealth,
    inPerson: row.inPerson,
    insurances: (row.insurances ?? []).map((i) => i.insurance?.name).filter(Boolean) as string[],
    insuranceIds: (row.insurances ?? []).map((i) => i.insurance?.id).filter(Boolean) as string[],
    insuranceOptions: (row.insurances ?? [])
      .map((i) => i.insurance)
      .filter((i): i is NonNullable<typeof i> => !!i?.id && !!i?.name)
      .map((i) => ({
        id: i.id,
        name: i.name,
        aliases: i.aliases ?? [],
        isPopular: i.isPopular === true,
        operators: i.operators ?? [],
      })),
    experience: row.experienceYears,
    slots: expandSlots(row.availability),
    bio: row.bio ?? '',
    videoUrl: row.videoUrl ?? '',
    socialLinks: row.socialLinks ?? {},
    consultationInfo: row.consultationInfo ?? {},
    languages: row.languages ?? [],
    mainConditions: row.mainConditions ?? [],
    otherConditions: row.otherConditions ?? [],
    education: row.education ?? '',
    experiences: row.experiences ?? '',
    awards: row.awards ?? [],
    publications: row.publications ?? [],
    whyBook: row.whyBook ?? [],
    aiSummary: row.aiSummary ?? '',
    reviewThemes: row.reviewThemes ?? [],
    addresses: Array.isArray(row.addresses) ? row.addresses.map(toAddress) : [],
    photoUrls: row.photoUrls ?? [],
    certificateUrls: row.certificateUrls ?? [],
    publicationLinks: asArray(row.publicationLinks),
    pressLinks: asArray(row.pressLinks),
    faq: asArray(row.faq),
    resultPhotos: asArray(row.resultPhotos),
    videos: asArray(row.videos),
  };
}

/** The historical single business zone. ponytail: migration scaffold — every call site
 *  that still passes this is a spot the booking-slot personalization will swap for the
 *  viewer's own stored zone (grep BUSINESS_TZ = the not-yet-personalized worklist).
 *  Kept as one named const so the required `tz` param stays explicit and greppable. */
export const BUSINESS_TZ = 'America/Sao_Paulo';

/** A malformed stored zone must never throw and 500 a page. The signup path can persist a
 *  non-IANA zone (it bypasses the API's @IsIn(IANA_ZONES) DTO), and Intl.DateTimeFormat
 *  throws RangeError on an unknown timeZone. Mirror timezone.ts's server contract on the
 *  web: fall back to the business zone. Probe is cheap; display formatting is not hot. */
function safeZone(tz: string): string {
  try {
    new Intl.DateTimeFormat('en-CA', { timeZone: tz });
    return tz;
  } catch {
    return BUSINESS_TZ;
  }
}

/** Numeric date/time parts in an EXPLICIT IANA zone — correct regardless of the (UTC)
 *  server TZ. Used for the picker round-trip (machine 'yyyy-mm-dd'/'HH:mm'), which is
 *  locale-independent; localized display goes through fmtDate instead. */
function tzParts(d: Date, tz: string) {
  const p = new Intl.DateTimeFormat('en-CA', {
    timeZone: safeZone(tz),
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  })
    .formatToParts(d)
    .reduce<Record<string, string>>((a, x) => ((a[x.type] = x.value), a), {});
  return { year: +p.year, month: +p.month, day: +p.day, hour: p.hour, minute: p.minute };
}

/** "HH:MM" in the given zone — correct on a UTC server (Vercel), unlike
 *  getHours()/getMinutes() which read the server's local clock (3h off for BRT).
 *  Time is locale-neutral so this takes no locale. */
export function fmtTime(d: Date, tz: string) {
  const p = tzParts(d, tz);
  return `${p.hour}:${p.minute}`;
}
/** Localized date string in the given zone. Replaces the old pt-BR-hardcoded fmtDatePtBR
 *  and the hand-rolled DAY/MON arrays, so en/es users finally get localized weekday and
 *  month names instead of Portuguese ones. `tz` and `locale` are both required so every
 *  call site names whose zone AND whose language it means. */
export function fmtDate(d: Date, opts: Intl.DateTimeFormatOptions, tz: string, locale: string) {
  return new Intl.DateTimeFormat(locale, { ...opts, timeZone: safeZone(tz) }).format(d);
}

/** Localized date+time in an explicit zone (rule 7) — replaces per-screen
 *  `new Intl.DateTimeFormat` copies, which each forgot the timeZone. */
export function fmtDateTime(
  d: Date,
  tz: string,
  locale: string,
  opts: Intl.DateTimeFormatOptions = { dateStyle: 'medium', timeStyle: 'short' },
) {
  return new Intl.DateTimeFormat(locale, { ...opts, timeZone: safeZone(tz) }).format(d);
}

/** THE 'date · time' stamp (audit: five hand-rolled granularities existed). */
export function fmtStamp(
  d: Date,
  tz: string,
  locale: string,
  style: 'short' | 'compact' | 'long' | 'withYear' = 'short',
) {
  const STYLES: Record<string, Intl.DateTimeFormatOptions> = {
    short: { weekday: 'short', day: 'numeric', month: 'short' },
    compact: { day: '2-digit', month: 'short' },
    long: { weekday: 'long', day: 'numeric', month: 'long' },
    withYear: { day: '2-digit', month: 'short', year: 'numeric' },
  };
  return `${fmtDate(d, STYLES[style], tz, locale)} · ${fmtTime(d, tz)}`;
}

/**
 * INVERSE of fmtTime/fmtDate: a picker's 'yyyy-mm-dd' + 'HH:mm' is a
 * wall-clock time in the given zone `tz` → UTC ISO.
 * `new Date(`${date}T${time}`)` silently used the BROWSER's zone, so a doctor
 * on a non-matching machine (e.g. EDT) had every entered time shifted on display.
 * The browser zone cancels out here, so this is correct anywhere.
 */
export function businessWallTimeToIso(date: string, time: string, tz: string): string {
  const [y, mo, d] = date.split('-').map(Number);
  const [h, mi] = time.split(':').map(Number);
  const naiveUtc = Date.UTC(y, mo - 1, d, h, mi);
  const at = new Date(naiveUtc);
  // Offset (ms) of tz vs UTC at that instant; both toLocaleString results are
  // re-parsed in the browser zone, which therefore cancels in the subtraction.
  const asTz = new Date(at.toLocaleString('en-US', { timeZone: tz })).getTime();
  const asUtc = new Date(at.toLocaleString('en-US', { timeZone: 'UTC' })).getTime();
  return new Date(naiveUtc - (asTz - asUtc)).toISOString();
}
